<script setup lang='ts'>
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import useResponsive from '@/composables/useResponsive';
import useRouting from '@/composables/useRouting';
import { useFiltersStore } from '@/stores/useFiltersStore';
import { useMetricsStore } from '@/stores/useMetricsStore';
import { getTranslatedName } from '@/utils/utils';

const slider = ref();
const { t } = useI18n();
const { isMobile } = useResponsive();

const metricsStore = useMetricsStore();
const { types } = storeToRefs(metricsStore);

const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

const { redirectOrFetch } = useRouting();

const matchingTypes = computed(() => types.value.filter(it => it.isMatching).sort((a, b) => {
  if (a.isSelected && !b.isSelected) {
    return -1; // a comes first
  }
  if (!a.isSelected && b.isSelected) {
    return 1; // b comes first (a comes after b)
  }
  return 0;
}));
const nonMatchingTypes = computed(() => types.value.filter(it => !it.isMatching));
const pictogramSize = computed(() => isMobile.value ? '40' : '50');

function getMatchingPicto(pictoStr: string | null): string {
  const pictogramToDisplay = pictoStr ?? '<svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">\n<g id="nouveau_picto">\n<path id="nouveau-picto" d="M46.22 71.15C50.39 71.83 54.66 71.29 58.52 69.57C62.38 67.86 65.65 65.06 67.94 61.51C70.23 57.96 71.43 53.82 71.4 49.6C71.37 45.38 70.1 41.26 67.76 37.75M52.94 28.25C48.79 27.62 44.55 28.2 40.73 29.93C36.91 31.66 33.67 34.47 31.41 38.01C29.15 41.55 27.97 45.66 28.01 49.86C28.05 54.06 29.3 58.15 31.62 61.65M56.19 49.7C56.19 53.29 53.28 56.2 49.69 56.2C46.1 56.2 43.19 53.29 43.19 49.7C43.19 46.11 46.1 43.2 49.69 43.2C53.28 43.2 56.19 46.11 56.19 49.7ZM69.19 34.53C69.19 36.92 67.25 38.86 64.86 38.86C62.47 38.86 60.53 36.92 60.53 34.53C60.53 32.14 62.47 30.2 64.86 30.2C67.25 30.2 69.19 32.14 69.19 34.53ZM38.85 64.86C38.85 67.25 36.91 69.19 34.52 69.19C32.13 69.19 30.19 67.25 30.19 64.86C30.19 62.47 32.13 60.53 34.52 60.53C36.91 60.53 38.85 62.47 38.85 64.86Z" stroke="black" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round"/>\n</g>\n</svg>';
  return createSvgFromStr(pictogramToDisplay);
}

function createSvgFromStr(svgString: string, width = 80, height = 80) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  const svgElement = doc.documentElement;
  if (svgElement.tagName.toLowerCase() !== 'svg') {
    console.error('The string provided is not a valid SVG.');
    return '';
  }
  if (width) {
    svgElement.setAttribute('width', width.toString());
  }
  if (height) {
    svgElement.setAttribute('height', height.toString());
  }
  if (!svgElement.hasAttribute('viewBox')) {
    svgElement.setAttribute('viewBox', '0 0 100 100');
  }
  const serializer = new XMLSerializer();
  const modifiedSvgStr = serializer.serializeToString(svgElement);
  const base64 = window.btoa(decodeURIComponent(encodeURIComponent(modifiedSvgStr)));
  return `data:image/svg+xml;base64,${base64}`;
}

function handleSlider(side: 'left' | 'right') {
  if (!slider.value)
    return;
  const distance = Math.floor(slider.value.clientWidth / 3);
  side === 'left' ? slider.value.scrollLeft -= distance : slider.value.scrollLeft += distance;
}

function setupTypes(type: any) {
  // REFACTORING de la feature => Debouncing multiple selections.

  // Toggle selected types into an array. Load the array with saved filters from localStorage, on mounted.
  // Sort types list alphabetically (non-moving type on selection).

  // Debouncing from array last change.
  // On debounce, all types in array are isSelected, others not isSelected.
  // On debounce, update filters and save them to LocalStorage.
  // Handle routing with new filters updated with new types selection.

  // Revalidate behaviour with non-matching types (single or multiples) ??
  // If one non-matching, reset filters with only this one, then routing to defaultRoute ??

  const currentSelection = filters.value.selections?.find(it => it.filterProperties.type === 'taxonomyTypes');
  if (type.isSelected && type.isMatching) {
    const typeFilterIndex = currentSelection?.selection?.findIndex((it: any) => it === type.id);
    if (typeFilterIndex > -1) {
      currentSelection!.selection = currentSelection!.selection.filter((it: any) => it !== type.id);
    }
    if (!currentSelection.selection.length) {
      filters.value.selections = filters.value.selections.filter((it: any) => it.filterProperties.type !== 'taxonomyTypes');
    }
  }

  if (!type.isSelected && type.isMatching) {
    if (currentSelection) {
      currentSelection.selection.push(type.id);
    }
    else {
      filters.value.selections?.push({
        filterProperties: {
          type: 'taxonomyTypes',
        },
        selection: [type.id],
      });
    }
  }

  if (!type.isMatching) {
    if (currentSelection) {
      currentSelection.selection = [type.id];
    }
    else {
      filters.value.selections?.push({
        filterProperties: {
          type: 'taxonomyTypes',
        },
        selection: [type.id],
      });
    }
    filters.value.selections = filters.value.selections.filter((it: any) => it.filterProperties.type !== 'taxonomyThemes');
    filters.value.metrics = filters.value.metrics.filter((it: any) => it.type !== 'crossedTaxonomyCount');
  }
  // Reset du search
  filtersStore.addSearchingFilter('');
  filters.value.sorts = filters.value.sorts.filter((it: any) => it.type !== 'search');

  type.isSelected = !type.isSelected;
  filtersStore.saveToStorage(filters.value);
  redirectOrFetch();
}

// DELETED IN RAPID QA BEFORE PUSH TO PROD. TO CONFIRM
// watch(
//   () => types.value,
//   () => slider.value.scrollTo({
//         left: 0,
//         behavior: 'smooth'
//     }),
//     { deep: true }
// )
</script>

<template>
  <section :aria-label="t('activities.label')" class="yb-preset">
    <div class="activities-wrapper">
      <button
        v-if="types.length > 0" type="button" class="arrow-left" :aria-label="t('activities.left')"
        @click="handleSlider('left')"
      >
        <ChevronLeft :size="26" color="var(--yb-color-black)" aria-hidden="true" />
      </button>
      <div ref="slider" class="slider">
        <a
          v-for="type in matchingTypes" :key="type.id" role="link" class="activity-block"
          :class="{ matching: type.isMatching, selected: type.isSelected }" tabindex="0" :aria-label="type.name"
          @click="setupTypes(type)" @keydown.enter="setupTypes(type)"
        >
          <img
            :src="getMatchingPicto(type.pictogram)" :height="pictogramSize" :width="pictogramSize"
            alt="pictogramme de l'activité" aria-hidden="true"
          >
          <h3>{{ getTranslatedName(type) }}</h3>
        </a>
        <div v-if="matchingTypes.length && nonMatchingTypes.length" class="slider_separator" />
        <a
          v-for="type in nonMatchingTypes" :key="type.id" role="link" class="activity-block"
          :class="{ matching: type.isMatching, selected: type.isSelected }" tabindex="0" :aria-label="type.name"
          @click="setupTypes(type)" @keydown.enter="setupTypes(type)"
        >
          <img
            :src="getMatchingPicto(type.pictogram)" :height="pictogramSize" :width="pictogramSize"
            alt="pictogramme de l'activité" aria-hidden="true"
          >
          <h3>{{ getTranslatedName(type) }}</h3>
        </a>
      </div>
      <button
        v-if="types.length > 0" type="button" class="arrow-right" :aria-label="t('activities.right')"
        @click="handleSlider('right')"
      >
        <ChevronRight :size="26" color="var(--yb-color-black)" aria-hidden="true" />
      </button>
    </div>
  </section>
</template>

<style lang="css" scoped>
section {
  min-width: 100%;
  display: flex;
  align-items: center;
  height: 88px;
  background-color: var(--yb-color-slidergray);
  border-block: 1px solid var(--yb-color-lightgray);

  @media screen and (width < 1024px) {
    height: 66px;
  }

  .activities-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    overflow-y: hidden;
    padding-inline: 30px;
    height: 100%;
    width: 100%;

    @media screen and (width < 1024px) {
      padding-inline: 0;
    }

    .arrow-left,
    .arrow-right {
      display: grid;
      place-items: center;
      aspect-ratio: 1;
      background-color: var(--yb-color-white);
      border: 1px solid var(--yb-color-mediumgray);
      padding: var(--yb-spacing-xs);
      border-radius: 9999%;
      cursor: pointer;
      transition: box-shadow 0.2s ease-out;

      &:focus,
      &:focus-visible {
        outline-color: var(--yb-outline);
      }

      &:hover {
        box-shadow:
          rgba(0, 0, 0, 0.05) 0 6px 24px 0,
          rgba(0, 0, 0, 0.08) 0 0 0 1px;
      }

      @media screen and (width < 1024px) {
        display: none;
      }
    }

    .arrow-left svg {
      position: relative;
      right: 1px;
    }

    .arrow-right svg {
      position: relative;
      left: 1px;
    }

    .slider {
      flex-grow: 1;
      display: flex;
      align-items: center;
      gap: var(--yb-spacing-2xl);
      width: min(100%, 1270px);
      min-height: 60px;
      height: 100%;
      margin-inline: var(--yb-spacing-base);
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scrollbar-width: none !important;
      scrollbar-color: var(--yb-color-slidergray) var(--yb-color-slidergray);
      scroll-behavior: smooth;

      /* @media screen and (width < 1024px) {
        padding-top: var(--yb-spacing-xs);
      } */

      .slider_separator {
        height: 50px;
        min-width: 1px;
        background-color: var(--yb-color-mediumgray);
      }

      .activity-block {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        color: var(--yb-color-black);
        scroll-snap-align: center;
        cursor: pointer;

        &:focus,
        &:focus-visible {
          outline-color: var(--yb-outline);
          outline-offset: 6px;
        }

        svg {
          pointer-events: none;
          stroke: var(--yb-color-mediumgray);
          stroke-width: 1px;
        }

        img {
          opacity: 0.4;
        }

        h3 {
          position: relative;
          color: var(--yb-color-mediumgray);
          font-size: var(--yb-text-sm);
          font-weight: var(--yb-text-light);
          white-space: nowrap;
          pointer-events: none;

          @media screen and (width < 640px) {
            font-size: var(--yb-text-xs);
          }
        }

        &.matching {
          img {
            opacity: 1;
          }

          h3 {
            color: var(--yb-color-black);
          }
        }

        &.selected {
          h3::after {
            position: absolute;
            content: '';
            bottom: -11px;
            left: 0;
            width: 100%;
            height: 3px;
            background-color: var(--yb-color-primary);
          }

          img {
            opacity: 1;
          }
        }
      }
    }
  }
}
</style>
